import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Channel, GlobalFilterParams } from '@/types';

export interface DateRange {
  dateFrom: string; // ISO yyyy-MM-dd (inclusive)
  dateTo: string; // ISO yyyy-MM-dd (inclusive)
}

export interface FilterState {
  brandIds: number[];
  businessUnitIds: number[];
  regionIds: number[];
  competitorBrandIds: number[];
  dateRange: DateRange;
  /** Communication channel; `undefined` = all channels (emails + text messages). */
  channel: Channel | undefined;
}

export interface FilterContextValue extends FilterState {
  /** Currently selected brand (single-select), if any. */
  brandId: number | undefined;
  /** Brand is single-select; pass `undefined` to clear it. */
  setBrandId: (id: number | undefined) => void;
  setBusinessUnitIds: (ids: number[]) => void;
  setRegionIds: (ids: number[]) => void;
  setCompetitorBrandIds: (ids: number[]) => void;
  setDateRange: (range: DateRange) => void;
  /** Pass `undefined` for all channels. */
  setChannel: (channel: Channel | undefined) => void;
  resetFilters: () => void;
  /** Filter state shaped for API query params. */
  toApiParams: () => GlobalFilterParams;
  activeFilterCount: number;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultDateRange(): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 90); // default: last 90 days
  return { dateFrom: isoDate(start), dateTo: isoDate(end) };
}

const defaultState: FilterState = {
  brandIds: [],
  businessUnitIds: [],
  regionIds: [],
  competitorBrandIds: [],
  dateRange: defaultDateRange(),
  channel: undefined,
};

const FilterContext = createContext<FilterContextValue | undefined>(undefined);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FilterState>(defaultState);

  const setBrandId = useCallback(
    (id: number | undefined) =>
      // Changing the brand invalidates any previously selected business units
      // and competitors: both dropdowns are scoped to the selected brand
      // (competitors are the other brands in the selected brand's team(s)).
      setState((s) =>
        s.brandIds[0] === id
          ? s
          : {
              ...s,
              brandIds: id === undefined ? [] : [id],
              businessUnitIds: [],
              competitorBrandIds: [],
            },
      ),
    [],
  );
  const setBusinessUnitIds = useCallback(
    (businessUnitIds: number[]) => setState((s) => ({ ...s, businessUnitIds })),
    [],
  );
  const setRegionIds = useCallback(
    (regionIds: number[]) => setState((s) => ({ ...s, regionIds })),
    [],
  );
  const setCompetitorBrandIds = useCallback(
    (competitorBrandIds: number[]) => setState((s) => ({ ...s, competitorBrandIds })),
    [],
  );
  const setDateRange = useCallback(
    (dateRange: DateRange) => setState((s) => ({ ...s, dateRange })),
    [],
  );
  const setChannel = useCallback(
    (channel: Channel | undefined) => setState((s) => ({ ...s, channel })),
    [],
  );
  const resetFilters = useCallback(
    () => setState({ ...defaultState, dateRange: defaultDateRange() }),
    [],
  );

  const value = useMemo<FilterContextValue>(() => {
    const toApiParams = (): GlobalFilterParams => ({
      brandIds: state.brandIds.length ? state.brandIds : undefined,
      businessUnitIds: state.businessUnitIds.length ? state.businessUnitIds : undefined,
      regionIds: state.regionIds.length ? state.regionIds : undefined,
      competitorBrandIds: state.competitorBrandIds.length ? state.competitorBrandIds : undefined,
      dateFrom: state.dateRange.dateFrom,
      dateTo: state.dateRange.dateTo,
      channel: state.channel,
    });
    const activeFilterCount =
      (state.brandIds.length ? 1 : 0) +
      (state.businessUnitIds.length ? 1 : 0) +
      (state.regionIds.length ? 1 : 0) +
      (state.competitorBrandIds.length ? 1 : 0) +
      (state.channel ? 1 : 0);
    return {
      ...state,
      brandId: state.brandIds[0],
      setBrandId,
      setBusinessUnitIds,
      setRegionIds,
      setCompetitorBrandIds,
      setDateRange,
      setChannel,
      resetFilters,
      toApiParams,
      activeFilterCount,
    };
  }, [
    state,
    setBrandId,
    setBusinessUnitIds,
    setRegionIds,
    setCompetitorBrandIds,
    setDateRange,
    setChannel,
    resetFilters,
  ]);

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

export function useFilters(): FilterContextValue {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters must be used within FilterProvider');
  return ctx;
}
